import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { useSubscription } from '../context/SubscriptionContext';
import { ArrowLeft, Trophy, Clock, Volume2, VolumeX, Trash2, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import UpgradeModal from '../components/UpgradeModal';
import './PuzzleGame.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOUNDS = {
  correct: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
  wrong: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
  victory: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',
  tick: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'
};

const fuzzyMatch = (input, answer) => {
  // Simple normalization - just lowercase and trim, keep all characters
  const normalizedInput = input.toLowerCase().trim();
  const normalizedAnswer = answer.toLowerCase().trim();
  
  // Exact match
  if (normalizedInput === normalizedAnswer) return true;
  
  // Check if answer is contained in input or vice versa (for flexibility)
  if (normalizedInput.includes(normalizedAnswer) || normalizedAnswer.includes(normalizedInput)) {
    return true;
  }
  
  // Levenshtein distance for typos
  const distance = levenshteinDistance(normalizedInput, normalizedAnswer);
  const maxDistance = Math.max(1, Math.floor(normalizedAnswer.length * 0.25));
  return distance <= maxDistance;
};

const levenshteinDistance = (str1, str2) => {
  const matrix = [];
  for (let i = 0; i <= str2.length; i++) matrix[i] = [i];
  for (let j = 0; j <= str1.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
      }
    }
  }
  return matrix[str2.length][str1.length];
};

function PuzzleGame() {
  const { user } = React.useContext(AuthContext);
  const { trackPlay, playedGamesCount, maxPlayedGames, canPlayMore, isPremium } = useSubscription();
  const [puzzles, setPuzzles] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [revealedSquares, setRevealedSquares] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeSquareIndex, setActiveSquareIndex] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showEmoji, setShowEmoji] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const loadPuzzles = async () => {
    try {
      const response = await axios.get(`${API}/puzzles`, { withCredentials: true });
      setPuzzles(response.data);
    } catch (error) {
      console.error('Failed to load puzzles:', error);
      toast.error('Failed to load puzzles');
    }
  };

  const deletePuzzle = async (puzzleId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this puzzle?')) return;
    
    try {
      await axios.delete(`${API}/puzzles/${puzzleId}`, { withCredentials: true });
      toast.success('Puzzle deleted successfully');
      loadPuzzles();
    } catch (error) {
      console.error('Failed to delete puzzle:', error);
      toast.error('Failed to delete puzzle');
    }
  };
  
  const playSound = (soundName) => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio(SOUNDS[soundName]);
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  const endGame = (won) => {
    setGameComplete(true);
    if (won) {
      setShowConfetti(true);
      playSound('victory');
      setTimeout(() => setShowConfetti(false), 5000);
    }
  };
  
  useEffect(() => { loadPuzzles(); }, []);
  
  useEffect(() => {
    if (gameStarted && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            endGame(false);
            return 0;
          }
          if (prev <= 10) playSound('tick');
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, timeLeft, endGame, playSound]);
  
  const startGame = async (puzzle) => {
    // Check if user can play before tracking
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Track play when user starts a puzzle
    const result = await trackPlay(puzzle.id, 'puzzle');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSelectedPuzzle(puzzle);
    setGameStarted(true);
    setRevealedSquares([]);
    setScore(0);
    setGameComplete(false);
    setShowConfetti(false);
    setActiveSquareIndex(null);
    setUserAnswer('');
    if (puzzle.timer_setting === '30s') setTimeLeft(30);
    else if (puzzle.timer_setting === '1min') setTimeLeft(60);
    else setTimeLeft(null);
  };
  
  const handleSquareClick = (index) => {
    if (revealedSquares.includes(index) || gameComplete) return;
    setActiveSquareIndex(index);
    setUserAnswer('');
  };
  
  const handleSubmitAnswer = () => {
    if (!userAnswer.trim() || activeSquareIndex === null) return;
    
    const currentSquare = selectedPuzzle.squares[activeSquareIndex];
    console.log('User answer:', userAnswer);
    console.log('Correct answer:', currentSquare.answer);
    console.log('Match result:', fuzzyMatch(userAnswer, currentSquare.answer));
    
    if (fuzzyMatch(userAnswer, currentSquare.answer)) {
      playSound('correct');
      setScore(score + 10);
      setShowEmoji({ type: 'correct', index: activeSquareIndex });
      setTimeout(() => setShowEmoji(null), 1500);
      
      const newRevealed = [...revealedSquares, activeSquareIndex];
      setRevealedSquares(newRevealed);
      setActiveSquareIndex(null);
      setUserAnswer('');
      
      if (newRevealed.length === selectedPuzzle.grid_size) {
        setTimeout(() => endGame(true), 800);
      }
    } else {
      playSound('wrong');
      setShowEmoji({ type: 'wrong', index: activeSquareIndex });
      setTimeout(() => setShowEmoji(null), 1500);
      toast.error('Try again!');
    }
  };
  
  const resetGame = () => {
    setGameStarted(false);
    setSelectedPuzzle(null);
    setRevealedSquares([]);
    setScore(0);
    setTimeLeft(null);
    setGameComplete(false);
    setShowConfetti(false);
    setActiveSquareIndex(null);
    setUserAnswer('');
  };
  
  const getGridDimensions = (gridSize) => {
    if (gridSize === 9) return { cols: 3, rows: 3 };
    if (gridSize === 16) return { cols: 4, rows: 4 };
    if (gridSize === 25) return { cols: 5, rows: 5 };
    return { cols: 3, rows: 3 };
  };
  
  if (!gameStarted) {
    return (
      <div className="puzzle-game-page">
        <div className="puzzles-container">
          <div className="page-header-row">
            <h1>🧩 Puzzle Games</h1>
            <Link to="/puzzle-builder" className="create-new-btn">
              <Plus size={20} />
              Create New Puzzle
            </Link>
          </div>
          <div className="puzzles-grid">
            {puzzles.length === 0 ? (
              <div className="empty-state">
                <p className="no-puzzles">No puzzles yet. Create your first puzzle!</p>
                <Link to="/puzzle-builder" className="btn btn-primary">
                  <Plus size={18} />
                  Create Puzzle
                </Link>
              </div>
            ) : (
              puzzles.map((puzzle) => (
                <motion.div key={puzzle.id} className="puzzle-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.02 }}>
                  <div className="puzzle-image"><img src={puzzle.puzzle_image_url} alt={puzzle.title} /></div>
                  <div className="puzzle-info">
                    <h3>{puzzle.title}</h3>
                    <div className="puzzle-details">
                      <span className="detail-badge">{puzzle.grid_size === 9 && '3×3'}{puzzle.grid_size === 16 && '4×4'}{puzzle.grid_size === 25 && '5×5'}</span>
                      <span className="detail-badge">{puzzle.squares.length} Squares</span>
                      {puzzle.timer_setting !== 'off' && <span className="detail-badge timer-badge"><Clock size={14} />{puzzle.timer_setting}</span>}
                    </div>
                    <div className="card-actions">
                      <Button onClick={() => startGame(puzzle)} className="play-btn" disabled={!isPremium && !canPlayMore}>Play Now</Button>
                      {puzzle.created_by === user?.id && (
                        <Button onClick={(e) => deletePuzzle(puzzle.id, e)} variant="destructive" className="delete-btn">
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
        
        {/* Upgrade Modal for play limit */}
        <UpgradeModal 
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          limitType="play"
          currentCount={playedGamesCount}
          maxCount={maxPlayedGames || 3}
        />
      </div>
    );
  }
  
  const { cols, rows } = getGridDimensions(selectedPuzzle.grid_size);
  
  return (
    <div className="puzzle-game-page">
      {showConfetti && <Confetti />}
      <div className="game-container">
        <div className="game-header">
          <Button onClick={resetGame} className="back-btn" variant="ghost"><ArrowLeft size={20} />Back</Button>
          <div className="game-stats">
            <div className="stat"><Trophy size={20} /><span>{score} pts</span></div>
            <div className="stat score-count">{revealedSquares.length}/{selectedPuzzle.grid_size}</div>
            {timeLeft !== null && <div className={`stat timer ${timeLeft <= 10 ? 'warning' : ''}`}><Clock size={20} /><span>{timeLeft}s</span></div>}
            <Button onClick={() => setSoundEnabled(!soundEnabled)} className="sound-toggle" variant="ghost">
              {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </Button>
          </div>
        </div>
        
        <h2 className="puzzle-title">{selectedPuzzle.title}</h2>
        
        <div className="puzzle-game-area" style={{ backgroundImage: `url(${selectedPuzzle.puzzle_image_url})` }}>
          <div className="puzzle-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
            {selectedPuzzle.squares.map((square, index) => (
              <motion.div
                key={index}
                className={`puzzle-square ${revealedSquares.includes(index) ? 'revealed' : ''} ${activeSquareIndex === index ? 'active' : ''}`}
                onClick={() => handleSquareClick(index)}
                animate={revealedSquares.includes(index) ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                {!revealedSquares.includes(index) && (
                  <div className="square-content">
                    <span className="square-question">{square.question}</span>
                  </div>
                )}
                {showEmoji && showEmoji.index === index && (
                  <motion.div className="emoji-popup" initial={{ scale: 0, y: 0 }} animate={{ scale: 1.5, y: -30 }} exit={{ opacity: 0 }}>
                    {showEmoji.type === 'correct' ? '⭐✨' : '❌😢'}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
        
        <AnimatePresence>
          {activeSquareIndex !== null && !gameComplete && (
            <motion.div className="question-dialog-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActiveSquareIndex(null)}>
              <motion.div className="question-dialog" initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 50 }} onClick={(e) => e.stopPropagation()}>
                <h3>Question</h3>
                <p className="dialog-question">{selectedPuzzle.squares[activeSquareIndex].question}</p>
                <Input value={userAnswer} onChange={(e) => setUserAnswer(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSubmitAnswer()} placeholder="Type your answer" className="answer-input" autoFocus />
                <div className="dialog-actions">
                  <Button onClick={() => setActiveSquareIndex(null)} variant="outline">Cancel</Button>
                  <Button onClick={handleSubmitAnswer} className="submit-btn">Submit</Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {gameComplete && (
          <div className="game-complete-overlay">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }} className="complete-content">
              <Trophy size={64} color="#FFD700" />
              <h2>🎉 Puzzle Complete!</h2>
              <p className="final-score">Final Score: {score} points</p>
              <Button onClick={resetGame} className="play-again-btn">Play Another Puzzle</Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PuzzleGame;